/**
 * Vue Router beforeEach guard -- `requireToken`.
 *
 * Extracted from router/index.js (issue #901, PR-12) so that the page-reload
 * re-hydration logic can be unit-tested in isolation. The vitest config has
 * no @vitejs/plugin-vue, so importing router/index.js (which pulls in many
 * .vue components) is not possible -- same constraint that drove the
 * `requireCapability.js` extraction in PR-7.
 *
 * Behaviour:
 *   1. Public paths (login, shop, password reset, etc.) bypass auth.
 *   2. No token/userId in localStorage -> redirect to /login.
 *   3. Vuex state.account already populated -> allow (in-session navigation).
 *   4. Token in localStorage but state.account empty (page reload) ->
 *      fetch /api/auth/me + accounts/:id in PARALLEL, then dispatch
 *      the PR-11 `{account, authResponse}` shape so both Vuex
 *      (state.account, used by App.vue and legacy role getters) AND
 *      Pinia (capabilities, used by requireCapability guards) hydrate.
 *      Either fetch failing -> clear localStorage and redirect to /login.
 *
 * Why both calls remain. /auth/me returns only the auth payload
 * (token, account_id, capabilities, distributor) -- it does NOT return the
 * full SAFRS-shaped account with role.name, cart, username, email that
 * legacy Vuex consumers (App.vue, isShopper / isSuperAdmin getters) rely
 * on. The accounts/:id call still feeds Vuex state.account; /auth/me
 * additionally feeds Pinia. Promise.all keeps the latency a single
 * round-trip.
 */

import {
    postLoginRedirect,
    loginRedirectFor,
    NEUTRAL_ENTRY_PATHS,
} from './postLoginRedirect'

// `loginRedirectFor` (the one builder of the /login?next= URL) and the
// neutral entry paths live with the rest of the post-login logic; re-exported
// so existing importers keep working.
export { loginRedirectFor }

/**
 * Resolve the role-appropriate landing for a neutral entry path and, if it
 * differs from where the user already is, redirect there.
 *
 * Reused by BOTH the in-session short-circuit (step 3) and the post-reload
 * rehydration path (step 4) so a logged-in user hitting `/` or `/admin` always
 * lands on their own surface -- not the legacy Owner CampaignList -- regardless
 * of whether they navigated within the session or hard-reloaded the tab.
 *
 * Capabilities come from Pinia (the PR-11 hydrate target). The store is lazily
 * imported so this module stays unit-testable without pulling Pinia into tests
 * that never exercise this branch.
 *
 * @param {object} to    The target route (`to.path` is matched against
 *                        NEUTRAL_ENTRY_PATHS).
 * @param {function} next Vue Router `next`.
 * @returns {Promise<boolean>} true if it consumed `next` (caller must return);
 *                             false if the path is not neutral / no redirect
 *                             applies (caller falls through to its own next()).
 */
async function dispatchNeutralEntry(to, next) {
    if (!NEUTRAL_ENTRY_PATHS.has(to.path)) {
        return false
    }
    return redirectToRoleLanding(to, next)
}

/**
 * Send the user to their role landing (`postLoginRedirect` over the Pinia
 * capabilities) unless they are already there.
 *
 * @returns {Promise<boolean>} true if it consumed `next`.
 */
async function redirectToRoleLanding(to, next) {
    let caps = []
    try {
        const { useAccountStore } = await import('@/stores/account')
        const accountStore = useAccountStore()
        caps = Array.isArray(accountStore?.capabilities)
            ? accountStore.capabilities
            : []
    } catch (_e) {
        // Pinia not initialised (e.g., in a stripped-down test). Let the
        // caller fall through to the original destination.
        return false
    }
    const dest = postLoginRedirect({ capabilities: caps })
    if (dest && dest !== to.path && dest !== '/login') {
        next(dest)
        return true
    }
    return false
}

/**
 * Hydrate ONLY the Pinia `useAccountStore` from a /auth/me payload, bypassing
 * the Vuex `login` action.
 *
 * Used by the reload path when the boot accounts/:id fetch failed (403/404 on
 * an impersonated/Owner-less tenant) but /auth/me succeeded. The Vuex `login`
 * action dereferences `account.campaign_id` and would crash on a null account,
 * so we cannot route through it; we still want Pinia capabilities populated so
 * requireCapability guards work. Lazily imported + try/catch wrapped so this
 * module stays unit-testable without pulling Pinia into tests that never
 * exercise this branch (same constraint as dispatchNeutralEntry).
 *
 * @param {object} authResponseData The `.data` of the /auth/me response.
 * @returns {Promise<void>}
 */
async function hydratePiniaFromAuthMe(authResponseData) {
    if (!authResponseData) {
        return
    }
    try {
        const { useAccountStore } = await import('@/stores/account')
        useAccountStore().hydrate(authResponseData)
    } catch (_e) {
        // Pinia not initialised (e.g., in a stripped-down test) or hydrate
        // unavailable. Best-effort; the session still proceeds on the valid
        // token. Intentionally swallowed.
    }
}

const PATHS_WITHOUT_AUTHENTICATION = [
    '/login',
    // Redirects to /pricing; public so a logged-out visitor reaches the redirect.
    '/signup',
    // Bare /shop is a redirect (router/redirects.js), not a mounted page,
    // since Phase D (ADR 0059) retired the generic_shop landing. It must
    // stay public: `beforeEach` runs against `to.fullPath` BEFORE Vue
    // Router follows the redirect, and the redirect's own destination
    // (`/shop/<slug>` or `/login`) is already public in its own right.
    '/shop',
    '/order_confirmation',
    '/password_reset',
    '/new-user',
    '/pricing',
    // The Master Services Agreement text (working draft). Readable by
    // anyone; it carries no tenant data. Its confirm control asks the
    // server who is signed in.
    '/legal/agreement',
    // Shopper help INDEX (`/help/shopper` with no trailing slug). The
    // article paths are covered by the `/help/shopper/` prefix entry in
    // PATH_PARAMS_WITHOUT_AUTHENTICATION below; this exact-match entry
    // covers the bare index, which that prefix does not reach.
    '/help/shopper',
]

const PATH_PARAMS_WITHOUT_AUTHENTICATION = [
    // `/shop/` and `/store/` are not listed: every storefront path is
    // handled before this list is consulted (STOREFRONT_PATH below).
    // Shopper help articles (`/help/shopper/<slug>`). Public by design:
    // `docs/user/HELP-SYSTEM-DESIGN.md` decision 4 requires the shopper
    // help surface to render WITHOUT a distributor login -- a shopper
    // reading "how do I place an order" is by definition not signed into
    // the distributor app, and bouncing them to `/login` would send them
    // to a page they can never satisfy.
    //
    // SCOPED TO `/help/shopper/` ON PURPOSE -- never a bare `/help/`.
    // This list is matched by PREFIX (`fullPath.substring(0, len)`), so a
    // bare `/help/` entry would silently make OWNER help public too. Owner
    // help lives at `/distributor/help/owner/*` inside the authenticated
    // distributor route group and must keep bouncing; the tests in
    // `__tests__/help-routes-auth-boundary.spec.js` lock both halves.
    //
    // Help carries no tenant data, so this is not a data-exposure seam:
    // the backend `/api/help/*` endpoints are public by the same design
    // decision.
    '/help/shopper/',
    '/password_reset/',
    '/sign-up/',
    '/new-user/',
    // Client Portal magic link (close-reminder email): the token IS the
    // credential and AutoLogin.vue redeems it for a session, so a signed-out
    // visitor must reach it. Matched by PREFIX: keep it exactly this deep so
    // `/portal` and the rest of the portal stay gated.
    '/portal/invite/',
    // Phase F5 / ADR 0050: Client Store Owner invitation magic-link
    // landing. The recipient may not yet have an account; the token IS
    // the credential. The accept POST mints the bearer; the landing
    // page itself is pre-auth.
    '/auth/accept-invitation/',
    // Email-change confirmation (card https://trello.com/c/BxskXBad). The
    // link is delivered ONLY to the new address and followed from that
    // mailbox -- routinely a browser with no session at all. The
    // single-use, expiring token IS the credential, exactly as for
    // '/password_reset/' directly above.
    '/confirm-email/',
]

// The storefront: `/shop/<slug>/...` and the legacy `/store/<slug>/...`.
//
// EVERY storefront path is left to StorefrontSessionGate (Jake, 2026-10-02:
// a shopper is never sent to the generic /login). The gate establishes the
// session -- token AND store id -- for every shopper page, the account pages
// send a visitor without a personal session to the STORE HOME
// (router/storefrontAccountGuard.js), and the emailed order link reads its
// order through its one-order code. Plan:
// docs/planning/2026-10-02-storefront-session-everywhere-plan.md
//
// This replaces two earlier storefront special cases here, both now the
// gate's job: re-gating orders / addresses / payment-methods / profile to
// /login (which bounced AH shoppers to the ThreadFlow sign-in, prod
// 2026-10-02), and re-hydrating a held token on the cart (card FSPLvtQK),
// which the gate now does for every shopper page.
const STOREFRONT_PATH = /^\/(?:shop|store)\//

/**
 * Build the guard. Dependencies (the Vuex store and the authMe service)
 * are passed in so tests can substitute mocks without resorting to module
 * mocking gymnastics.
 *
 * @param {object} deps
 * @param {object} deps.store        Vuex store instance.
 * @param {function} deps.authMe     Returns a Promise resolving to the
 *                                   /api/auth/me axios response (`.data`
 *                                   contains the payload).
 * @param {function} [deps.deeplink] Deep-link auto-impersonation step,
 *                                   (route) -> 'proceed'|'landing'|'reloading'
 *                                   (router/deeplinkImpersonationRuntime.js).
 *                                   Runs once per page load, on the first
 *                                   navigation after rehydration.
 * @returns {function} Vue Router beforeEach guard.
 */
export function createRequireToken({ store, authMe, deeplink = null }) {
    // Closure-scoped in-flight promise. Vue Router fires beforeEach for each
    // guarded layer when hard-reloading into a nested route -- without this
    // guard each invocation independently dispatches /auth/me + accounts/:id
    // and races duplicate `login` actions. See project memory
    // `feedback_inflight-promise-guard-pattern.md` (originated in PR #738
    // for DistributorAccountInfo). Scoping to the factory closure (rather
    // than module level) keeps test instances isolated.
    let inFlight = null

    // Deep-link auto-impersonation runs on the FIRST navigation of a page
    // load only (Jake, 2026-10-27: "the same resolution runs on every
    // deep-link load"): a pasted or clicked link is a page load, while later
    // in-app navigation stays inside the seat the user already holds. The
    // guard closure is rebuilt on every page load, so this resets with it.
    // Set before the await so a concurrent invocation cannot run it twice.
    let deeplinkChecked = false

    // NOTE (2026-08-26): there is deliberately NO "this fetch is known to be
    // failing" memo here any more. `bootAccountKnownFailing` used to live at
    // this spot: a boolean latched to true by the first 403/404 on the boot
    // accounts/:id fetch, after which every subsequent navigation skipped the
    // fetch outright. It was removed, and it should not come back. See the
    // block in the rehydration cycle below for the full three-layer history.

    return async function requireToken(to, _from, next) {
        // ---- 1. Public-path bypass ---------------------------------------
        // The storefront first: the session gate owns it (see above).
        if (STOREFRONT_PATH.test(to.path || to.fullPath || '')) {
            return next()
        }
        const matchesParamPath = PATH_PARAMS_WITHOUT_AUTHENTICATION.some(
            (route) => to.fullPath.substring(0, route.length) === route
        )
        // Exact-match entries are compared on the PATH ONLY. Comparing the
        // full path made `/login?next=...` (the bounce's own destination)
        // look protected, so the guard bounced it again and dropped `next`.
        const pathOnly = String(to.fullPath).split(/[?#]/)[0]
        const isPublicPath =
            PATHS_WITHOUT_AUTHENTICATION.indexOf(pathOnly) > -1 ||
            matchesParamPath
        if (isPublicPath) {
            return next()
        }

        // ---- 2. No credentials at all -> /login --------------------------
        const token = localStorage.getItem('token')
        const userId = localStorage.getItem('userId')
        if (!token || !userId) {
            // Carry where they were going (#3088) so an emailed deep
            // link survives the sign-in it triggers.
            return next(loginRedirectFor(to.fullPath))
        }

        // ---- 3. Already authenticated this session -> allow --------------
        // On a neutral entry path, dispatch to the role-appropriate surface
        // first (same logic as the post-reload path below). This closes the
        // admin-vs-distributor landing bug: pre-fix, in-session navigation to
        // bare `/` short-circuited straight to next() and every role landed on
        // the legacy Owner CampaignList, while `/admin` hit the legacy
        // `/admin -> /distributor` bounce so a Super Admin never reached their
        // own dashboard.
        if (store.state.account) {
            if (await dispatchNeutralEntry(to, next)) {
                return
            }
            return next()
        }

        // ---- 4. Page reload -- re-hydrate both stores ---------------------
        // Run both calls in parallel:
        //   - /auth/me        -> Pinia capabilities (PR-11 shape)
        //   - accounts/:id    -> Vuex state.account (legacy consumers)
        // If either fails (typically 401 from /auth/me on expired token),
        // dispatch logout (clears auth-owned keys + Pinia + Vuex) and
        // redirect.
        //
        // Concurrency: a single rehydration cycle can be triggered by
        // multiple beforeEach invocations (nested-route hard reload). The
        // `inFlight` cache below coalesces them into one Promise.all so we
        // don't dispatch duplicate `login` actions or duplicate XHRs.
        if (!inFlight) {
            inFlight = (async () => {
                try {
                    // /auth/me is the session-liveness authority: await it
                    // FIRST and on its own. A rejection here (401 / expired /
                    // network) means the token is dead -> fall to the catch
                    // below (logout + /login). We do NOT collapse it into a
                    // single Promise.all with accounts/:id, because that would
                    // make an accounts/:id rejection (403/404) indistinguishable
                    // from a dead session and nuke a perfectly valid token.
                    //
                    // The account fetch still fires in PARALLEL (kicked off
                    // before the await) so latency stays a single round-trip;
                    // we just settle it separately so its failure can be
                    // tolerated independently.
                    //
                    // ALWAYS fetch. There is no skip here -- neither a
                    // guessed one nor a remembered one. That is the whole
                    // point of this block, and it is the end state of a bug
                    // that had to be fixed in THREE layers. Read this before
                    // adding any condition to the dispatch below.
                    //
                    // THE SYMPTOM (Jake, 2026-08-26). An SA impersonating a
                    // distributor clicks Add to Cart and gets "We couldn't
                    // find your session to add items to your cart." Both
                    // storefront handler gates on the Vuex account id
                    // (``state.account._jv.id`` --
                    // pages/store/ProductPage.vue) and raises that snackbar
                    // BEFORE any API call. So anything that leaves
                    // ``state.account`` null breaks add-to-cart outright.
                    // Whenever this fetch is skipped, ``dispatch('login')``
                    // never runs and ``state.account`` stays null.
                    //
                    // LAYER 1 -- the server (PR #2997, commit 543d61718).
                    // ``RoleMixin.get_query()`` scoped the SAFRS Account GET
                    // by ``_distributor_perms``, so an impersonating SA's own
                    // ``distributor_id IS NULL`` row failed its own filter and
                    // ``GET /api/accounts/<SA id>`` 404'd. Fixed by re-admitting
                    // ``cls.id == g.account.id`` for ``Account`` under
                    // impersonation; that endpoint now returns 200.
                    //
                    // LAYER 2 -- the preemptive skip (PR #3006, ef2dbd2b8).
                    // This block used to skip the fetch OUTRIGHT whenever the
                    // impersonation breadcrumb (IMPERSONATION_NAME_KEY) was in
                    // localStorage, to keep one red 404 out of the console on
                    // the first load into an impersonation. Cosmetic, and it
                    // encoded the layer-1 bug as a permanent assumption -- so
                    // after #2997 it was suppressing a fetch that would have
                    // SUCCEEDED. Removed. Regression net:
                    // ``requireToken-impersonation-hydrates-account.spec.js``.
                    //
                    // LAYER 3 -- the latch (this change). A closure-scoped
                    // ``bootAccountKnownFailing`` flag was set to true by the
                    // first 403/404 and never reset, so every later navigation
                    // skipped the fetch. It was defended as "evidence-based,
                    // therefore safe" -- but ONE observation is not evidence
                    // about all future requests, and the flag had no way to
                    // notice the backend being repaired underneath it. The
                    // guard closure is built once per page load
                    // (``createRequireToken`` at module scope in
                    // router/index.js), so a tab that latched against the
                    // pre-#2997 404 kept skipping for the tab's whole
                    // lifetime, surviving ordinary SPA navigation and reload.
                    // Only closing the tab cleared it. That is why Jake still
                    // saw the original symptom against fully-fixed, deployed
                    // code. Regression net:
                    // ``requireToken-boot-account-latch-recovery.spec.js``.
                    //
                    // WHY REMOVED RATHER THAN MADE SELF-HEALING. The obvious
                    // alternative was to keep the latch and reset it on an
                    // impersonation start/stop. That reset would be DEAD CODE:
                    // both transitions are hard navigations
                    // (``startImpersonation`` -> ``window.location.reload()``,
                    // the banner's exit -> ``window.location.assign()``), which
                    // destroy the closure and the flag with it. A latch whose
                    // only reset path cannot fire on a live closure is just the
                    // permanent latch again. A TTL would be a second timing
                    // mechanism guarding a fetch that is already cheap.
                    //
                    // WHAT IT COSTS. For a row that genuinely cannot resolve
                    // (an Owner-less tenant), one 403/404 per navigation
                    // instead of one per page load. That is the case the latch
                    // was built for, and it is worth paying: the failure is
                    // tolerated a few lines below (the session survives on
                    // /auth/me alone), the request is a single cheap
                    // round-trip already issued in parallel with /auth/me, and
                    // console noise is not worth a silently broken cart.
                    // Covered by the "degrades safely on EVERY navigation"
                    // test in the latch-recovery spec.
                    const accountPromise = store.dispatch('jv/get', [
                        `accounts/${userId}`,
                        { params: { include: 'role' } },
                    ])
                    // Pre-attach a no-op catch so a rejection that lands before
                    // we await accountPromise does not surface as an unhandled
                    // rejection while authMe() is still pending.
                    accountPromise.catch(() => {})

                    const meResp = await authMe()

                    // authMe() succeeded -> the token is valid and the session
                    // is alive. Now resolve the boot account fetch; tolerate
                    // its failure without killing the session.
                    let account = null
                    let accountFetchFailed = false
                    try {
                        account = await accountPromise
                    } catch (_accountErr) {
                        // P3 (impersonation defense-in-depth): a 403/404 on the
                        // boot accounts/:id fetch (e.g. a SuperAdmin impersonating
                        // a tenant whose effective caps lack account.view, or an
                        // Owner-less tenant where the record 404s) must NOT be
                        // treated as a dead session. authMe() already proved the
                        // token valid. For ANY account-fetch error we keep the
                        // session alive (token is valid; a transient/permission
                        // account-fetch failure should not log the user out) --
                        // 403/404 is the expected impersonation case, and a
                        // transient 5xx is even less reason to discard a valid
                        // token. We skip the legacy Vuex state.account hydration
                        // and proceed on Pinia capabilities from /auth/me alone.
                        //
                        // The status is deliberately NOT inspected. It used to
                        // be, to promote a 403/404 into the permanent
                        // ``bootAccountKnownFailing`` latch -- see the
                        // three-layer history above. Every failure is now
                        // treated the same way: tolerate it, and try again on
                        // the next navigation. That is what lets a session
                        // recover in place when the row becomes readable
                        // (a backend fix, a permission grant, a transient 5xx
                        // clearing) instead of staying broken until the tab is
                        // closed.
                        accountFetchFailed = true
                    }

                    if (accountFetchFailed) {
                        // No account record -> nothing to compare for the
                        // cross-tab bleed check (that check needs BOTH sides),
                        // and login(account) would crash on a null account
                        // (it dereferences account.campaign_id). Hydrate Pinia
                        // capabilities directly from the auth response so
                        // requireCapability guards work, then proceed.
                        await hydratePiniaFromAuthMe(meResp?.data)
                        return { ok: true }
                    }

                    // P1-1: cross-tab session bleed defence (only meaningful
                    // when BOTH /auth/me AND accounts/:id succeeded).
                    // /auth/me uses the LIVE token (axios interceptor) so its
                    // account_id is the server's authoritative answer for
                    // who the current token authorizes. accounts/:id was
                    // dispatched with the userId we read from localStorage
                    // upfront -- if another tab logged out + re-logged as
                    // a different user between those reads, we would mix
                    // Bob's capabilities (Pinia) with Alice's role/account
                    // (Vuex). Fail closed on any mismatch.
                    //
                    // jsonapi-vuex (`jv/get`) returns the resolved record
                    // with the JSON:API id at `account._jv.id` (string), NOT
                    // at `account.id`. Reading `account?.id` always yields
                    // undefined -> mismatch always fires -> every page reload
                    // logs out. Confirmed live on the rearch sandbox
                    // 2026-04-28; original PR-12 unit tests passed because
                    // their fakes used a flat `{ id, ... }` shape.
                    const jvAccountId = account?._jv?.id ?? account?.id
                    if (
                        meResp?.data?.account_id != null &&
                        String(meResp.data.account_id) !== String(jvAccountId)
                    ) {
                        throw new Error(
                            'requireToken: account_id mismatch between /auth/me and accounts/:id (cross-tab session change)'
                        )
                    }

                    await store.dispatch('login', {
                        account,
                        authResponse: meResp.data,
                    })
                    return { ok: true }
                } catch (err) {
                    // Token invalid / session expired / network error /
                    // cross-tab mismatch.
                    //
                    // P1-2: do NOT call localStorage.clear() -- that would
                    // wipe unrelated keys (cart drafts, shopper preferences).
                    // The Vuex `logout` action enumerates exactly the
                    // auth-owned keys (token, userId, roleId, roleName,
                    // distributorId, campaignId, firstName, lastName,
                    // companyEmail, selected{Department,Manager,Location})
                    // and clears Pinia. Single source of truth for what
                    // "logged out" means.
                    //
                    // P2-D: wrap logout in its own try/catch. If logout
                    // itself rejects (low likelihood -- local-state only --
                    // but defense-in-depth matters), the surrounding IIFE
                    // would reject and propagate to all concurrent awaiters,
                    // surfacing as an unhandled error in Vue Router. Swallow
                    // silently: recovery from a failed-rehydrate path
                    // shouldn't be blocked by a logout dispatch failure.
                    // The outer flow continues to next('/login') below.
                    try {
                        await store.dispatch('logout')
                    } catch (_logoutErr) {
                        // Best-effort cleanup; intentionally swallowed.
                    }
                    return { ok: false, err }
                } finally {
                    inFlight = null
                }
            })()
        }

        const result = await inFlight
        if (!result.ok) {
            // A stale session on a deep link is the SAME situation as no
            // session: re-authenticate, then continue to the target.
            return next(loginRedirectFor(to.fullPath))
        }

        // Deep-link auto-impersonation (see router/deeplinkImpersonation.js):
        // put the user in the seat that owns this resource page, or send
        // them to their role landing when no seat of theirs can see it.
        if (deeplink && !deeplinkChecked) {
            deeplinkChecked = true
            let outcome = 'proceed'
            try {
                outcome = await deeplink(to)
            } catch (_e) {
                outcome = 'landing'
            }
            if (outcome === 'reloading') {
                // A hard navigation is replacing the page.
                return next(false)
            }
            if (outcome === 'landing' && (await redirectToRoleLanding(to, next))) {
                return
            }
        }

        // Route-fork PR-R2: post-reload landing.
        //
        // After a successful rehydration on a "neutral" entry path (bare `/`
        // or `/admin`), route the user to their role-appropriate surface via
        // `postLoginRedirect`. This mirrors the TheLoginPage.vue dispatch so
        // that a Client Admin who reloads the tab at `/` lands on `/portal`
        // rather than the legacy Owner CampaignList -- same for a Shopper
        // hitting `/admin` directly. Same helper as the in-session path above.
        if (await dispatchNeutralEntry(to, next)) {
            return
        }

        return next()
    }
}
